import { useEffect, useMemo } from "react";

import { useBxpWebStargateClient } from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";

import { getOrgQueryParam } from "@/common/utils";
import {
    useFastshiftDispatch,
    useFastshiftSelector,
    FastshiftDispatchPromise,
    TABLE_IDS
} from "@/state";
import * as StateActions from "@/state/actions";

/**
 * Fetches support requests data with deduplication and abort cleanup.
 * Prevents redundant API calls by checking lastFetchParams.
 */
export const useFetchSupportRequests = () => {
    const dispatch = useFastshiftDispatch();
    const stargateClient = useBxpWebStargateClient();

    const { data: customerDomainData } = useFastshiftSelector(
        (state) => state.customerDomain
    );

    const selectedDomainOrg = useFastshiftSelector(
        (state) => state.selectedDomainOrg
    );

    const { lastFetchParams } = useFastshiftSelector(
        (state) => state.supportRequests
    );

    const supportTicketQueryParams = useMemo(() => {
        return selectedDomainOrg ? getOrgQueryParam(selectedDomainOrg) : {};
    }, [selectedDomainOrg]);

    useEffect(() => {
        let supportRequestsPromise: FastshiftDispatchPromise<unknown> | null =
            null;

        if (customerDomainData && selectedDomainOrg) {
            const needsFetch =
                !lastFetchParams ||
                lastFetchParams.customerDomainId !==
                    customerDomainData.customerDomainId ||
                lastFetchParams.queryParams.orgId !==
                    supportTicketQueryParams.orgId ||
                lastFetchParams.queryParams.permissionLevel !==
                    supportTicketQueryParams.permissionLevel;

            if (!needsFetch) {
                return;
            }

            // Clear migration tickets table controls when fetching new data (org changed)
            dispatch(
                StateActions.clearTableControls({
                    tableId: TABLE_IDS.MIGRATION_TICKETS
                })
            );

            supportRequestsPromise = dispatch(
                StateActions.getFastshiftSupportRequests({
                    stargateClient,
                    customerDomainId: customerDomainData.customerDomainId,
                    queryParams: supportTicketQueryParams
                })
            );
        }

        return () => {
            if (supportRequestsPromise) {
                supportRequestsPromise.abort();
            }
        };
    }, [
        dispatch,
        stargateClient,
        customerDomainData,
        selectedDomainOrg,
        supportTicketQueryParams,
        lastFetchParams
    ]);
};
